import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileSignature, ShieldCheck, LogOut, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContractorContract = ({ onSigned, onLogout }) => {
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contractor/contract/me`)
      .then(({ data }) => { setData(data); setName(data.name || ""); })
      .catch(() => toast.error("Could not load your contract."));
  }, []);

  const sign = async () => {
    if (!accepted) return toast.error("Please tick the box to accept the terms.");
    if (!name.trim()) return toast.error("Please type your full name to sign.");
    setSigning(true);
    try {
      await axios.post(`${API}/contractor/contract/sign`, {
        accepted: true,
        name: name.trim(),
        signed_date: new Date().toLocaleDateString("en-AU"),
      });
      toast.success("Contract signed — welcome aboard!");
      onSigned?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSigning(false);
    }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-8 w-8 animate-spin text-blaze" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-4 py-8 md:py-12" data-testid="contractor-contract-page">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blaze text-white shadow-lg">
              <FileSignature className="h-5 w-5" />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold text-slate-900 md:text-2xl">Contractor Agreement</h1>
              <p className="text-xs text-slate-500">Please read and sign to access your dashboard.</p>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 active:scale-95" data-testid="contract-logout-btn">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2.5" data-testid="contract-highlights">
          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
            <p className="font-display text-2xl font-bold text-blaze">{data.payment_period_days}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Day pay period</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
            <p className="font-display text-2xl font-bold text-amber-500">${data.no_show_fee}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">No-show fee</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
            <p className="font-display text-2xl font-bold text-blaze">{data.notice_days}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Day notice to leave</p>
          </div>
        </div>

        <div className="mt-5 max-h-[46vh] space-y-4 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="contract-terms">
          {data.terms.map((t, i) => (
            <div key={i} data-testid={`contract-clause-${i}`}>
              <p className="font-display text-sm font-bold text-slate-900">{t.title}</p>
              <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-slate-600">{t.body}</p>
            </div>
          ))}
        </div>

        {data.signed ? (
          <div className="mt-5 flex items-center gap-2 rounded-2xl border border-green-300 bg-green-50 p-4 text-green-700" data-testid="contract-already-signed">
            <CheckCircle2 className="h-5 w-5" /> Signed on {data.signed_date}. Refreshing…
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Type your full name to sign</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full legal name"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-blaze"
              data-testid="contract-name-input"
            />
            <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-sm text-slate-700">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blaze" data-testid="contract-accept-checkbox" />
              <span>I, <strong>{name || "……"}</strong>, have read and accept all the terms of this Contractor Agreement, including the {data.payment_period_days}-day payment period, the ${data.no_show_fee} late-cancellation / no-show fee, and the {data.notice_days}-day notice required to end the contract.</span>
            </label>
            <button
              onClick={sign}
              disabled={signing || !accepted || !name.trim()}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-3.5 text-xs font-bold uppercase tracking-widest text-white active:scale-95 disabled:opacity-40"
              data-testid="contract-sign-btn"
            >
              {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Sign & Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
